import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';

const STORAGE_KEY = 'sonor:cookie-consent:v1';

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function writeStored(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (e) {
    // localStorage may be unavailable (private mode); silently ignore
  }
}

const CookieBanner = () => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ functionality: false, analytics: false, marketing: false });

  useEffect(() => {
    const stored = readStored();
    if (!stored) {
      setOpen(true);
    } else if (stored.prefs) {
      setPrefs(stored.prefs);
    }

    const reopen = () => {
      const cur = readStored();
      if (cur && cur.prefs) setPrefs(cur.prefs);
      setExpanded(true);
      setOpen(true);
    };
    window.addEventListener('sonor:open-cookie-preferences', reopen);
    return () => window.removeEventListener('sonor:open-cookie-preferences', reopen);
  }, []);

  const persist = useCallback((next) => {
    writeStored({ prefs: next, ts: new Date().toISOString() });
    setPrefs(next);
    setOpen(false);
    setExpanded(false);
  }, []);

  const acceptAll = () => persist({ functionality: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ functionality: false, analytics: false, marketing: false });
  const saveSelected = () => persist(prefs);

  if (!open) return null;

  return (
    <Wrap role="dialog" aria-live="polite" aria-label="Cookie preferences">
      <Inner>
        {!expanded ? (
          <>
            <Body>
              <Title>We use cookies.</Title>
              <Text>
                We use strictly necessary cookies to run sonorai.co, and, only
                with your consent, functionality, analytics, and marketing
                cookies. Read our <a href="#cookies">Cookie Policy</a> for
                details.
              </Text>
            </Body>
            <Actions>
              <BtnGhost type="button" onClick={() => setExpanded(true)}>
                Manage preferences
              </BtnGhost>
              <BtnGhost type="button" onClick={rejectAll}>Reject</BtnGhost>
              <BtnDark type="button" onClick={acceptAll}>Accept all</BtnDark>
            </Actions>
          </>
        ) : (
          <ExpandedWrap>
            <Body>
              <Title>Manage cookie preferences</Title>
              <Text>
                Strictly necessary cookies are always on. You can choose which
                other categories to allow. You can change your choice at any
                time from the Cookie Policy page.
              </Text>
            </Body>
            <Rows>
              <Row>
                <RowLabel>
                  <RowName>Strictly necessary</RowName>
                  <RowDesc>Required for the site to function.</RowDesc>
                </RowLabel>
                <Pill>Always on</Pill>
              </Row>
              <Row as="label" htmlFor="ck-functionality">
                <RowLabel>
                  <RowName>Functionality</RowName>
                  <RowDesc>Remember choices for an enhanced experience.</RowDesc>
                </RowLabel>
                <input
                  id="ck-functionality"
                  type="checkbox"
                  checked={prefs.functionality}
                  onChange={(e) => setPrefs({ ...prefs, functionality: e.target.checked })}
                />
              </Row>
              <Row as="label" htmlFor="ck-analytics">
                <RowLabel>
                  <RowName>Analytics</RowName>
                  <RowDesc>Aggregated usage to help us improve the site.</RowDesc>
                </RowLabel>
                <input
                  id="ck-analytics"
                  type="checkbox"
                  checked={prefs.analytics}
                  onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                />
              </Row>
              <Row as="label" htmlFor="ck-marketing">
                <RowLabel>
                  <RowName>Marketing</RowName>
                  <RowDesc>Measure campaigns and ad effectiveness.</RowDesc>
                </RowLabel>
                <input
                  id="ck-marketing"
                  type="checkbox"
                  checked={prefs.marketing}
                  onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
                />
              </Row>
            </Rows>
            <Actions>
              <BtnGhost type="button" onClick={rejectAll}>Reject all</BtnGhost>
              <BtnGhost type="button" onClick={acceptAll}>Accept all</BtnGhost>
              <BtnDark type="button" onClick={saveSelected}>Save my choices</BtnDark>
            </Actions>
          </ExpandedWrap>
        )}
      </Inner>
    </Wrap>
  );
};

const Wrap = styled.div`
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 200;
  display: flex;
  justify-content: center;
  pointer-events: none;
`;

const Inner = styled.div`
  pointer-events: auto;
  width: 100%;
  max-width: 820px;
  background: #FFFFFF;
  color: var(--fg-1);
  border: 1px solid var(--line-2);
  border-radius: 14px;
  box-shadow: 0 24px 60px -20px rgba(0, 0, 0, 0.18);
  padding: 18px 18px;
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 16px;
  }
`;

const ExpandedWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

const Body = styled.div`
  flex: 1 1 320px;
  min-width: 240px;
`;

const Title = styled.div`
  font-family: var(--body);
  font-size: 16px;
  font-weight: 600;
  color: var(--fg-0);
  margin: 0 0 4px;
`;

const Text = styled.p`
  font-family: var(--body);
  font-size: 13.5px;
  line-height: 1.55;
  color: var(--fg-2);
  margin: 0;

  a { color: var(--blue); text-decoration: underline; }
  a:hover { color: var(--fg-0); }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;

  @media (max-width: 640px) {
    justify-content: stretch;
    & > button { flex: 1 1 auto; }
  }
`;

const BtnBase = `
  font-family: var(--body);
  font-size: 13.5px;
  font-weight: 500;
  padding: 9px 14px;
  border-radius: 9999px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.18s, color 0.18s, border-color 0.18s;
`;

const BtnDark = styled.button`
  ${BtnBase}
  background: var(--fg-0);
  color: #FFFFFF;
  border: 1px solid var(--fg-0);
  &:hover { background: var(--blue); border-color: var(--blue); }
`;

const BtnGhost = styled.button`
  ${BtnBase}
  background: transparent;
  color: var(--fg-1);
  border: 1px solid var(--line-2);
  &:hover { color: var(--fg-0); border-color: var(--fg-0); }
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-top: 1px solid var(--line-2);
  border-bottom: 1px solid var(--line-2);
  padding: 4px 0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 4px;
  cursor: pointer;

  & + & { border-top: 1px solid var(--line-1); }

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
    accent-color: var(--fg-0);
    cursor: pointer;
  }
`;

const RowLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const RowName = styled.span`
  font-family: var(--body);
  font-size: 14px;
  font-weight: 600;
  color: var(--fg-0);
`;

const RowDesc = styled.span`
  font-family: var(--body);
  font-size: 12.5px;
  color: var(--fg-2);
`;

const Pill = styled.span`
  font-family: var(--mono);
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.28px;
  color: var(--fg-3);
  padding: 4px 10px;
  border: 1px solid var(--line-2);
  border-radius: 9999px;
`;

export default CookieBanner;
